/**
 * @typedef {"fa-2xs" | "fa-xs" | "fa-sm" | "fa-lg" | "fa-xl" | "fa-2xl"} TSizeIcon
 */

/**
 * @typedef {"image" | "video" | "soft-image" | "audio" | "application" | "pdf" | "csv" | "json"} TTypeFile
 */

/**
 * @typedef {"word" | "powerpoint" | "excel" | "zip"} TApplicationType
 */

const ICONS = Object.freeze({
  audio: "fa-solid fa-file-audio",
  csv: "fa-solid fa-file-csv",
  file: "fa-solid fa-file",
  image: "fa-solid fa-file-image",
  video: "fa-solid fa-film",
  pdf: "fa-solid fa-file-pdf",
  powerpoint: "fa-solid fa-file-powerpoint",
  excel: "fa-solid fa-file-excel",
  word: "fa-solid fa-file-word",
  zip: "fa-solid fa-file-zipper",
  json: "fa-solid fa-file-code",
});

const IMAGE_TYPES = {
  bmp: "image/bmp",
  bmp_raw: "bmp",
  gif: "image/gif",
  gif_raw: "gif",
  jpeg: "image/jpeg",
  jpg: "image/jpg",
  jpeg_raw: "jpeg",
  jpg_raw: "jpg",
  png: "image/png",
  png_raw: "png",
  "svg+xml": "image/svg+xml",
  svg: "svg",
  tiff: "image/tiff",
  tiff_raw: "tiff",
  webp: "image/webp",
  webp_raw: "webp",
};

const SOFT_IMAGE_TYPES = {
  jpeg: "image/jpeg",
  jpg: "image/jpg",
  jpeg_raw: "jpeg",
  jpg_raw: "jpg",
  png: "image/png",
  png_raw: "png",
  "svg+xml": "image/svg+xml",
  svg: "svg",
  webp: "image/webp",
  webp_raw: "webp",
};

const AUDIO_TYPES = {
  mpeg: "audio/mpeg",
  "x-m4a": "audio/x-m4a",
};

const VIDEO_TYPES = {
  mp4: "video/mp4",
  mp4_raw: "mp4",
  avi: "video/x-msvideo",
  mpeg: "video/mpeg",
  mpeg_raw: "mpeg",
  ogv: "video/ogg",
  ogv_raw: "ogg",
};

export const APPLICATION = {
  word: {
    docx: "docx",
    odt: "odt",
    msword: "application/msword",
    wordprocessingml:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    opendocument: "application/vnd.oasis.opendocument.text",
  },
  powerpoint: {
    pptx: "pptx",
    ppt: "ppt",
    "vnd.apple.keynote": "application/vnd.apple.keynote",
    "vnd.ms-powerpoint": "application/vnd.ms-powerpoint",
    presentation: "application/vnd.oasis.opendocument.presentation",
    presentationml:
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  },
  excel: {
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    xls: "application/vnd.ms-excel",
    xls_raw: "xls",
    xlsx_raw: "xlsx",
  },
  zip: {
    zip: "application/zip",
    zip_raw: "zip",
    "7z": "application/x-7z-compressed",
    "7z_raw": "7z",
    rar: "application/vnd.rar",
    rar_raw: "rar",
    tar: "application/x-tar",
    tar_raw: "tar",
  },
};

const JSON_TYPES = {
  json: "application/json",
  raw: "json",
};

const PDF = {
  pdf: "application/pdf",
  raw: "pdf",
};

const CSV = {
  csv: "text/csv",
  raw: "csv",
};

/**
 * @param {string} [format=""]
 * @returns {string}
 */
export function typeFileIs(format = "") {
  const cleanedFormat = String(format).toLowerCase().trim();

  if (Object.values(JSON_TYPES).includes(cleanedFormat)) return "json";
  if (Object.values(CSV).includes(cleanedFormat)) return "csv";
  if (Object.values(PDF).includes(cleanedFormat)) return "pdf";
  if (Object.values(IMAGE_TYPES).includes(cleanedFormat)) return "image";
  if (Object.values(AUDIO_TYPES).includes(cleanedFormat)) return "audio";
  if (Object.values(VIDEO_TYPES).includes(cleanedFormat)) return "video";
  if (Object.values(APPLICATION.word).includes(cleanedFormat)) return "word";
  if (Object.values(APPLICATION.excel).includes(cleanedFormat)) return "excel";
  if (Object.values(APPLICATION.zip).includes(cleanedFormat)) return "zip";
  if (Object.values(APPLICATION.powerpoint).includes(cleanedFormat)) {
    return "powerpoint";
  }

  return "file";
}

/**
 * @param {string} [format=""]
 * @param {TSizeIcon} [size]
 * @returns {string}
 */
export function getFileTypeIcon(format = "", size) {
  const formattedSize = " " + (size || "");

  switch (typeFileIs(format)) {
    case "image":
      return ICONS.image.concat(formattedSize);

    case "video":
      return ICONS.video.concat(formattedSize);

    case "audio":
      return ICONS.audio.concat(formattedSize);

    case "pdf":
      return ICONS.pdf.concat(formattedSize);

    case "word":
      return ICONS.word.concat(formattedSize);

    case "csv":
      return ICONS.csv.concat(formattedSize);

    case "powerpoint":
      return ICONS.powerpoint.concat(formattedSize);

    case "excel":
      return ICONS.excel.concat(formattedSize);

    case "zip":
      return ICONS.zip.concat(formattedSize);

    case "json":
      return ICONS.json.concat(formattedSize);

    default:
      return ICONS.file.concat(formattedSize);
  }
}

/**
 * @param {TTypeFile | string} prefix
 * @param {TApplicationType} [application]
 * @returns {string}
 */
export function getType(prefix, application) {
  switch (prefix) {
    case "image":
      return Object.values(IMAGE_TYPES).join(",");

    case "video":
      return Object.values(VIDEO_TYPES).join(",");

    case "soft-image":
      return Object.values(SOFT_IMAGE_TYPES).join(",");

    case "audio":
      return Object.values(AUDIO_TYPES).join(",");

    case "application":
      if (!application) return "*";
      return Object.values(APPLICATION[application]).join(",");

    case "pdf":
      return Object.values(PDF).join(",");

    case "csv":
      return Object.values(CSV).join(",");

    case "json":
      return Object.values(JSON_TYPES).join(",");

    default:
      return prefix;
  }
}

/**
 * @param {string} fileName
 * @returns {string | undefined}
 */
export function retrieveTruncatedFileExtension(fileName) {
  const parsedName = String(fileName);
  const length = parsedName.length;
  const last = length - 5;

  const parseExtension = (name = "") => name.split(".");

  return parseExtension(parsedName).length > 2
    ? parseExtension(parsedName.substring(last)).pop()
    : parseExtension(parsedName).pop();
}
